import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { track } from '@/lib/analytics';
import { Button, Card, Input } from '@/components';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { apiJson } from '@/lib/api';

const TOTAL_STEPS = 11;

function ProfileOnboardingPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track screen view
  useEffect(() => {
    track('First Last Name Email Screen');
  }, []);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
      };
      return apiJson(getToken, '/v1/user', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      // Navigate will be handled by the app layout's onboarding logic
      navigate({ to: '/app/onboarding/invite' });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Couldn't save your info. Please try again.";
      setError(message);
    },
  });

  const isFormValid = useMemo(() => {
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      email.trim().length > 0 &&
      email.includes('@')
    );
  }, [firstName, lastName, email]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      setError('Please fill in all fields with a valid email.');
      return;
    }
    setError('');
    mutation.mutate();
  };

  return (
    <div className='min-h-screen bg-sky-light'>
      <OnboardingProgress currentStep={8} totalSteps={TOTAL_STEPS} />
      <div className='max-w-md mx-auto px-6 py-12'>
        {/* Title */}
        <div className='text-center mb-8'>
          <h1
            className='text-3xl font-semibold text-gray-900 mb-3'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Tell us about yourself
          </h1>
          <p
            className='text-lg text-gray-500'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            This helps personalize your household and receipts.
          </p>
        </div>

        {/* Form */}
        <Card>
          <form onSubmit={handleSubmit} className='space-y-4'>
            <Input
              label='First name'
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder='Jordan'
              disabled={mutation.isPending}
              autoFocus
            />

            <Input
              label='Last name'
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder='Lee'
              disabled={mutation.isPending}
            />

            <Input
              label='Email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder='jordan@example.com'
              disabled={mutation.isPending}
            />

            {error && (
              <p className='text-sm text-red-500 text-center'>{error}</p>
            )}

            <div className='pt-2'>
              <Button
                type='submit'
                fullWidth
                disabled={mutation.isPending || !isFormValid}
                loading={mutation.isPending}
              >
                {mutation.isPending ? 'Saving…' : 'Continue'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/profile')({
  component: ProfileOnboardingPage,
});
